import React, { FC, PropsWithChildren } from 'react';

import { getUsernameCookie } from '@lucid/utils';
import { ComponentImage, SiteNav, SiteNavOptions } from '@lucid/components-react';

import {
  useMenuTopNavigation,
  useMenuProductSuite,
  useMenuIntegrationPpcNavbar,
  useMenuEnterpriseNavbar,
} from 'hooks/use-menu';
import useNavButtons, { placeholderButtons, placeholderLinks } from 'hooks/use-nav-buttons';
import type { I18n } from 'types/types';

export interface NavbarOptionalProps extends SiteNavOptions {
  /** Checks if the navbar is on a visio page. */
  isVisio?: boolean;
  /** The Drupal Node ID */
  nid?: string;
  /** The page type. */
  pageType?: string;
  /** Checks if we should hide the signup button. */
  shouldHideSignUpButton?: boolean;
}

export interface NavbarProps extends NavbarOptionalProps {
  /** The aria label for screen readers */
  buttonAriaLabel: string;
  /** The i18n translation data. */
  i18nData: I18n.DataEdges;
  /** The logo to be displayed in the Navbar. */
  logo: ComponentImage;
  /** The language currently set for the page context. */
  languageKey: I18n.AvailableLanguagesKey;
}

const Navbar: FC<PropsWithChildren<NavbarProps>> = ({
  buttonAriaLabel,
  entityTranslations = [],
  i18nData,
  isVisio = false,
  languageKey,
  logo,
  nid = '',
  pageType,
  shouldHideSignUpButton,
  ...rest
}) => {
  const navbarMenu = useMenuTopNavigation(languageKey);
  const topNavMenu = useMenuProductSuite(languageKey);
  const enterpriseNavbarMenu = useMenuEnterpriseNavbar(languageKey);
  const integrationPPCNavbarMenu = useMenuIntegrationPpcNavbar(languageKey);
  let chartNavbarMenu;

  switch (pageType) {
    case 'fifteen-integration-ppc':
      chartNavbarMenu = integrationPPCNavbarMenu;
      break;
    case 'enterprise':
      chartNavbarMenu = enterpriseNavbarMenu;
      break;
    default:
      chartNavbarMenu = navbarMenu;
  }

  const excludeTopNavOn = [
    'ppc-diagram-landing-page',
    'competitor-ppc',
    'fifteen-og-ppc',
    'consideration-ppc',
    'enterprise',
    'twentyone-enterprise',
    'competitor',
    'consideration',
    'consideration-competitor',
    'discovery',
    'fifteen-og',
    'single-marketo-form',
  ];
  const hasTopNav = !excludeTopNavOn.includes(pageType || '');

  // for now we are using addOn template to create some Cloud Insight pages.
  // These pages need a bit of customization for the nav so we don't show the pricing
  // link or "Sign up free" as the main nav CTA - this remove the pricing link
  // and change "Sign up free" into "Contact sales"
  const removeAddOnPricingLinkNids = ['4225'];
  if (pageType === 'addOn' && removeAddOnPricingLinkNids.includes(nid)) {
    chartNavbarMenu = navbarMenu.filter(link => link.entityId !== '644');
  }

  const removeNineteenSolutionsPricingLinkNids = ['4172'];
  if (pageType === 'nineteenSolutions' && removeNineteenSolutionsPricingLinkNids.includes(nid)) {
    chartNavbarMenu = navbarMenu.filter(link => link.entityId !== '644');
  }

  const username = getUsernameCookie();
  const initialNavButtons = useNavButtons(username, isVisio, pageType === 'discovery', shouldHideSignUpButton);

  return (
    <SiteNav
      {...rest}
      isSticky
      willHaveShadowOnScroll
      buttonAriaLabel={buttonAriaLabel}
      buttons={initialNavButtons.navButtons}
      entityTranslations={entityTranslations}
      hasTopNav={hasTopNav}
      i18nData={i18nData}
      items={chartNavbarMenu}
      logo={logo}
      placeholderButtons={placeholderButtons}
      placeholderLinks={placeholderLinks}
      shouldHideSignUpButton={shouldHideSignUpButton}
      topButtons={initialNavButtons.topNavButtons || []}
      topItems={topNavMenu}
    />
  );
};

export default Navbar;
